import type { JsonObject } from "./types";

export type AgentRole = "main_gm" | "creator_copilot" | "player_ooc" | "helper";
export type WorkerRole = "retrieval" | "research" | "compiler" | "world_sync" | "memory" | "image" | "index";
export type AgentActionStatus = "planned" | "running" | "completed" | "failed" | "denied" | "cancelled";
export type ToolRisk = "read" | "write" | "destructive";

export interface ToolContract {
  id: string;
  name: string;
  version: number;
  description: string;
  risk: ToolRisk;
  allowedAgentRoles: AgentRole[];
  requiredArgumentKeys: string[];
  idempotent: boolean;
  mutatesState: boolean;
  /** Human approval policy enforced by the runtime, not by prompt wording. */
  approval?: "never" | "write" | "always";
  /** Optional allowlist for arguments. Unknown keys are rejected when set. */
  allowedArgumentKeys?: string[];
}

export interface ToolInvocation {
  id: string;
  runId: string;
  toolContractId: string;
  agentRole: AgentRole;
  arguments: JsonObject;
  expectedRevision?: number;
  idempotencyKey?: string;
  createdAt: string;
}

export interface AgentActionRecord {
  id: string;
  runId: string;
  parentActionId?: string;
  agentRole: AgentRole;
  workerRole?: WorkerRole;
  title: string;
  status: AgentActionStatus;
  toolInvocationId?: string;
  targetFileIds: string[];
  changedFieldCount: number;
  summary?: string;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export interface AgentRunRecord {
  id: string;
  experienceId: string;
  sessionId?: string;
  agentRole: AgentRole;
  requestSummary: string;
  status: AgentActionStatus;
  actionIds: string[];
  sourceMessageIds: string[];
  createdAt: string;
  completedAt?: string;
}

export interface ToolValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateToolInvocation(contract: ToolContract, invocation: ToolInvocation): ToolValidationResult {
  const errors: string[] = [];
  if (invocation.toolContractId !== contract.id) errors.push("tool contract id does not match");
  if (!contract.allowedAgentRoles.includes(invocation.agentRole)) errors.push(`${invocation.agentRole} cannot use ${contract.name}`);
  for (const key of contract.requiredArgumentKeys) if (!Object.hasOwn(invocation.arguments, key)) errors.push(`missing argument: ${key}`);
  if (contract.allowedArgumentKeys) for (const key of Object.keys(invocation.arguments)) {
    if (!contract.allowedArgumentKeys.includes(key)) errors.push(`unknown argument: ${key}`);
  }
  if (contract.mutatesState && invocation.expectedRevision == null) errors.push("state mutation requires expectedRevision");
  if (contract.idempotent && !invocation.idempotencyKey?.trim()) errors.push("idempotent tool requires idempotencyKey");
  return { ok: errors.length === 0, errors };
}

export function summarizeAgentActions(actions: readonly AgentActionRecord[]) {
  return actions.reduce((summary, action) => {
    summary.total += 1;
    summary[action.status] += 1;
    summary.changedFiles += action.targetFileIds.length;
    summary.changedFields += action.changedFieldCount;
    return summary;
  }, {
    total: 0,
    planned: 0,
    running: 0,
    completed: 0,
    failed: 0,
    denied: 0,
    cancelled: 0,
    changedFiles: 0,
    changedFields: 0,
  });
}
